import { t } from "i18next";
import { IPv4, IPv6, Validator } from "ip-num";
import { assoc, isNil, isNotNil } from "ramda";
import * as yup from "yup";

const toLabelAndValue = value => ({
  label: humanizeIpAddressFormat(value),
  value,
});

const isValidIPv4String = ip =>
  isNotNil(ip) && Validator.isValidIPv4String(ip)[0];

const isValidIPv6String = ip =>
  isNotNil(ip) && Validator.isValidIPv6String(ip)[0];

const isValidIpString = ip => isValidIPv4String(ip) || isValidIPv6String(ip);

const areIpAddressesInSameFormat = (ipStart, ipEnd) =>
  (isValidIPv4String(ipStart) && isValidIPv4String(ipEnd)) ||
  (isValidIPv6String(ipStart) && isValidIPv6String(ipEnd));

const isValidIPv4Range = (ipStart, ipEnd) => {
  const ipStartAddr = IPv4.fromDecimalDottedString(ipStart);
  const ipEndAddr = IPv4.fromDecimalDottedString(ipEnd);

  return ipStartAddr.isLessThanOrEquals(ipEndAddr);
};

const isValidIPv6Range = (ipStart, ipEnd) => {
  const ipStartAddr = IPv6.fromString(ipStart);
  const ipEndAddr = IPv6.fromString(ipEnd);

  return ipStartAddr.isLessThanOrEquals(ipEndAddr);
};

const isValidIPRange = (ipStart, ipEnd) => {
  if (isValidIPv4String(ipStart) && isValidIPv4String(ipEnd)) {
    return isValidIPv4Range(ipStart, ipEnd);
  }

  if (isValidIPv6String(ipStart) && isValidIPv6String(ipEnd)) {
    return isValidIPv6Range(ipStart, ipEnd);
  }

  return false;
};

yup.addMethod(yup.string, "validIp", function (errorMessage) {
  return this.test("valid-ip", errorMessage, function (value) {
    if (isNil(value)) {
      return this;
    }

    const isValidIp = isValidIpString(value);

    return (
      isValidIp || this.createError({ path: this.path, message: errorMessage })
    );
  });
});

export const getIpRestrictionValidationSchema = () =>
  yup.object().shape({
    ipStart: yup
      .string()
      .validIp(t("neetoMolecules.ipRestriction.validations.invalidIp"))
      .required(t("neetoMolecules.ipRestriction.validations.ipStartRequired")),
    ipEnd: yup
      .string()
      .validIp(t("neetoMolecules.ipRestriction.validations.invalidIp"))
      .when("ipStart", (ipStart, schema) =>
        schema.test({
          test: ipEnd => areIpAddressesInSameFormat(ipStart, ipEnd),
          message: t(
            "neetoMolecules.ipRestriction.validations.eitherIpv4OrIpv6"
          ),
        })
      )
      .when("ipStart", (ipStart, schema) =>
        schema.test({
          test: ipEnd => isValidIPRange(ipStart, ipEnd),
          message: t(
            "neetoMolecules.ipRestriction.validations.ipEndGreaterThanIpStart"
          ),
        })
      )
      .required(t("neetoMolecules.ipRestriction.validations.ipEndRequired")),
    addressType: yup
      .object()
      .shape({
        value: yup.string().required(),
        label: yup.string().required(),
      })
      .nullable()
      .required(
        t("neetoMolecules.ipRestriction.validations.addressTypeRequired")
      ),
  });

export const humanizeIpAddressFormat = addressFormat => {
  const addressFormats = { ipv4: "IPv4", ipv6: "IPv6" };

  return addressFormats[addressFormat];
};

export const buildInitialValue = (isIpRestrictionEnabled, allowedIpRanges) => ({
  ipRestriction: isIpRestrictionEnabled,
  allowedIpRanges: buildAllowedIpRangesFormData(allowedIpRanges),
});

export const buildAllowedIpRangesFormData = allowedIpRanges =>
  allowedIpRanges?.map(allowedIpRange =>
    assoc(
      "addressType",
      toLabelAndValue(allowedIpRange.addressType),
      allowedIpRange
    )
  );

export const buildIpRestrictionPayload = values => {
  const allowedIpRangesWithTransformedAddressType = values.allowedIpRanges.map(
    allowedIpRange =>
      assoc("addressType", allowedIpRange.addressType.value, allowedIpRange)
  );

  return {
    isIpRestrictionEnabled: values.ipRestriction,
    allowedIpRanges: allowedIpRangesWithTransformedAddressType,
  };
};
